import { atom } from '@reatom/framework'
import { useAtom, useCtx } from '@reatom/npm-react'
import React, { useEffect, useMemo, useRef } from 'react'
import styled, { useTheme } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'
import { CanvasPnlPositionBuilder } from '@root/features/trade/position/pnl-card/ui/canvas-pnl-position-builder'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage } from '@root/shared/ui-kit'

import type { Pair } from '@root/entities/pair'
import type { WalletPositionModel } from '@root/entities/position'
import type { ReferralCode } from '@root/entities/referral/model/referral-code'
import type { UserShort } from '@root/entities/user-short'
import type { Error } from '@root/shared'

type Props = {
  position: WalletPositionModel
  pair: Pair.PairMinimal
  rendererRef?: React.MutableRefObject<CanvasPnlPositionBuilder | undefined>
  user: UserShort.UserShort
  referralCode: ReferralCode | undefined
}

const noopStateAtom = atom(
  AsyncStateN.notInitiated<Error.UnknownError, undefined>(),
  'noopStateAtom',
)

export const PnlPositionCardCanvas: React.FC<Props> = ({
  position,
  referralCode,
  rendererRef,
  user,
  pair,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const ctx = useCtx()

  const pnlRenderer = useMemo(
    () =>
      new CanvasPnlPositionBuilder(
        position,
        user,
        referralCode,
        {
          'common.total-profit': t('common.total-profit'),
          'common.total-invested': t('common.total-invested'),
          'common.total-sold': t('common.total-sold'),
        },
        ctx,
        pair,
        theme,
      ),
    [ctx, pair, position, referralCode, t, theme, user],
  )

  const [state] = useAtom(noopStateAtom)

  if (rendererRef) {
    rendererRef.current = pnlRenderer
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }

    pnlRenderer.setCanvas(canvas).build()
  }, [pnlRenderer])

  const renderOverlay = () => {
    if (AsyncStateN.isLoading(state)) {
      return (
        <InfoContent>
          <CircularLoader />
        </InfoContent>
      )
    }

    if (AsyncStateN.isError(state)) {
      return (
        <InfoContent>
          <InfoMessage intent="error" />
        </InfoContent>
      )
    }

    return undefined
  }

  return (
    <Root>
      <Canvas ref={canvasRef} />
      {renderOverlay()}
    </Root>
  )
}

const Root = styled.div`
  width: 100%;
`

const Canvas = styled.canvas`
  width: 100%;
`

const InfoContent = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  place-content: center;
`
